import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/auth/hooks';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import {
  Crown,
  AlertCircle,
  Check,
  X,
  CreditCard,
  Calendar,
  Zap,
  Shield,
  RefreshCw,
  Play,
  Clock,
  ArrowRight,
  TrendingUp,
  CheckCircle2,
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import InvoiceList from '../../components/subscription/InvoiceList';

interface SubscriptionOverview {
  subscription: {
    subscription_id: string;
    plan_name: string;
    plan_code: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
    is_active: boolean;
    is_expired: boolean;
    days_until_renewal: number;
    current_price: number;
    billing_cycle: string;
    payment_method_brand?: string;
    payment_method_last4?: string;
    plan_features: Record<string, boolean>;
    plan_limits: Record<string, number | null>;
    subscription_created_at?: string;
    canceled_at?: string;
  };
  usage: Record<string, number>;
  health_status: string;
}

const featureNameMap: Record<string, string> = {
  'staff_members': 'חברי צוות',
  'appointments': 'תורים',
  'customers': 'לקוחות',
  'services': 'שירותים',
  'products': 'מוצרים',
  'loyalty_program': 'תוכנית נאמנות',
  'ai_assistant': 'עוזר AI',
  'analytics': 'ניתוח נתונים',
  'whatsapp': 'WhatsApp',
  'invoices': 'חשבוניות'
};

export default function SubscriptionManagement() {
  const { business } = useAuth();
  const [overview, setOverview] = useState<SubscriptionOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);

  useEffect(() => {
    loadSubscriptionOverview();
  }, [business?.id]);

  async function loadSubscriptionOverview() {
    if (!business?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_subscription_overview', {
        p_business_id: business.id,
      });

      if (error) throw error;
      setOverview(data);
    } catch (error: any) {

      toast.error('שגיאה בטעינת פרטי המנוי');
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(action: string) {
    if (!business?.id) return;

    setActionLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-lifecycle`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            action,
            businessId: business.id,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to perform action');
      }

      const result = await response.json();

      if (action === 'cancel') {
        toast.success('המנוי יבוטל בסוף התקופה הנוכחית');
      } else if (action === 'reactivate') {
        toast.success('המנוי הופעל מחדש בהצלחה');
      } else {
        toast.success(result.message || 'פעולה בוצעה בהצלחה');
      }

      await loadSubscriptionOverview();
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בביצוע הפעולה');
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!overview?.subscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md w-full">
          <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">אין מנוי פעיל</h2>
          <p className="text-sm text-gray-600 mb-6">בחר חבילה שמתאימה לך</p>
          <Link
            to="/plan-upgrade"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl active:bg-blue-700 transition-colors w-full"
          >
            <Crown className="w-4 h-4" />
            בחר חבילה
          </Link>
        </div>
      </div>
    );
  }

  const sub = overview.subscription;
  const daysLeft = Math.max(0, Math.floor(sub.days_until_renewal));
  const isExpiringSoon = daysLeft <= 7;

  const usagePercentage = (feature: string) => {
    const limit = sub.plan_limits[feature];
    const usage = overview.usage[feature] || 0;
    if (!limit) return 0;
    return Math.min((usage / limit) * 100, 100);
  };

  const totalUsagePercent = () => {
    const usageItems = Object.entries(sub.plan_limits).filter(([_, limit]) => limit !== null);
    if (usageItems.length === 0) return 0;
    const avgPercent = usageItems.reduce((sum, [feature, limit]) => {
      return sum + usagePercentage(feature);
    }, 0) / usageItems.length;
    return Math.round(avgPercent);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-24">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-blue-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/settings" className="text-gray-500 active:text-gray-700">
              <ArrowRight className="h-5 w-5" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">ניהול מנוי</h1>
          </div>
          <button
            onClick={loadSubscriptionOverview}
            className="p-2 text-gray-500 active:text-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg overflow-hidden">
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">{sub.plan_name}</h2>
                  <p className="text-xs text-blue-100">{sub.plan_code}</p>
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                sub.is_active
                  ? 'bg-green-400/30 text-white border border-green-300/30'
                  : 'bg-white/20 text-white border border-white/30'
              }`}>
                {sub.is_active ? 'פעיל' : 'לא פעיל'}
              </div>
            </div>

            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-2xl font-bold text-white">${sub.current_price}</span>
              <span className="text-sm text-blue-100">
                / {sub.billing_cycle === 'monthly' ? 'חודש' : 'שנה'}
              </span>
            </div>

            {sub.subscription_created_at && (
              <p className="text-xs text-blue-100">
                חבר מאז {new Date(sub.subscription_created_at).toLocaleDateString('he-IL', {
                  month: 'long', year: 'numeric'
                })}
              </p>
            )}
          </div>

          {sub.cancel_at_period_end && (
            <div className="px-5 py-3 bg-orange-500/20 backdrop-blur-sm border-t border-orange-400/30">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-white flex-shrink-0" />
                <p className="text-xs text-white font-medium">
                  מתוכנן לביטול ב-{new Date(sub.current_period_end).toLocaleDateString('he-IL')}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-semibold text-gray-900">תקופת חיוב</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${
              isExpiringSoon ? 'bg-orange-50' : 'bg-green-50'
            }`}>
              <Clock className={`w-4 h-4 ${isExpiringSoon ? 'text-orange-600' : 'text-green-600'}`} />
              <span className={`text-sm font-bold ${isExpiringSoon ? 'text-orange-700' : 'text-green-700'}`}>
                עוד {daysLeft} ימים
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <span className="text-sm text-gray-600">תחילת תקופה</span>
              <span className="text-sm font-semibold text-gray-900">
                {new Date(sub.current_period_start).toLocaleDateString('he-IL')}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <span className="text-sm text-gray-600">סוף תקופה</span>
              <span className="text-sm font-semibold text-gray-900">
                {new Date(sub.current_period_end).toLocaleDateString('he-IL')}
              </span>
            </div>
          </div>
        </div>

        {sub.payment_method_brand && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-50 rounded-xl">
                <CreditCard className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">אמצעי תשלום</div>
                <div className="flex items-center gap-1.5 text-sm text-gray-600 mt-1">
                  <Shield className="w-3.5 h-3.5" />
                  <span>{sub.payment_method_brand} •••• {sub.payment_method_last4}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-gray-900">שימוש במכסה</span>
              </div>
              <div className="px-3 py-1 bg-white rounded-lg shadow-sm">
                <span className="text-sm font-bold text-blue-600">{totalUsagePercent()}%</span>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="space-y-4">
              {Object.entries(sub.plan_limits).map(([feature, limit]) => {
                const usage = overview.usage[feature] || 0;
                const percentage = limit ? usagePercentage(feature) : 0;
                const isUnlimited = limit === null;
                const isWarning = percentage >= 80;
                const displayName = featureNameMap[feature] || feature;

                return (
                  <div key={feature}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{displayName}</span>
                      {isUnlimited ? (
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                          ללא הגבלה
                        </span>
                      ) : (
                        <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                          isWarning
                            ? 'text-orange-600 bg-orange-50'
                            : 'text-blue-600 bg-blue-50'
                        }`}>
                          {usage} / {limit}
                        </span>
                      )}
                    </div>

                    {!isUnlimited && (
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isWarning
                              ? 'bg-gradient-to-r from-orange-400 to-orange-500'
                              : 'bg-gradient-to-r from-blue-400 to-blue-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5">
          <button
            onClick={() => setShowInvoices(!showInvoices)}
            className="flex items-center justify-between w-full mb-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">חשבוניות</div>
                <div className="text-xs text-gray-600">היסטוריית תשלומים</div>
              </div>
            </div>
            <motion.div
              animate={{ rotate: showInvoices ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ArrowRight className="w-5 h-5 text-gray-400" />
            </motion.div>
          </button>

          <AnimatePresence>
            {showInvoices && business?.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <InvoiceList
                  businessId={business.id}
                  businessName={business.name || 'העסק שלי'}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="space-y-3 pt-2">
          <Link
            to="/plan-upgrade"
            className="flex items-center justify-between w-full p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold">שדרג חבילה</div>
                <div className="text-xs text-blue-100">גישה לתכונות נוספות</div>
              </div>
            </div>
          </Link>

          {sub.cancel_at_period_end ? (
            <button
              onClick={() => handleAction('reactivate')}
              disabled={actionLoading}
              className="flex items-center justify-between w-full p-4 bg-green-600 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Play className="w-5 h-5" />
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">הפעל מחדש</div>
                  <div className="text-xs text-green-100">בטל את הביטול</div>
                </div>
              </div>
            </button>
          ) : (
            <button
              onClick={() => setShowCancelDialog(true)}
              disabled={actionLoading}
              className="flex items-center justify-between w-full p-4 bg-white border-2 border-red-200 text-red-600 rounded-xl shadow-sm active:bg-red-50 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <X className="w-5 h-5" />
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">בטל מנוי</div>
                  <div className="text-xs text-red-500">נשאר פעיל עד סוף התקופה</div>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showCancelDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCancelDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full"
            >
              <div className="p-6">
                <div className="flex items-start gap-3 mb-5">
                  <div className="p-3 bg-red-100 rounded-xl">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">ביטול מנוי</h3>
                    <p className="text-sm text-gray-600">האם אתה בטוח?</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-5 space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">נשאר פעיל</p>
                      <p className="text-xs text-gray-600">עד {new Date(sub.current_period_end).toLocaleDateString('he-IL')}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">החיוב יופסק</p>
                      <p className="text-xs text-gray-600">לא תחויב יותר</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">אפשר לחזור בך</p>
                      <p className="text-xs text-gray-600">בכל עת עד הביטול</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCancelDialog(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl active:bg-gray-200 transition-colors"
                  >
                    חזור
                  </button>
                  <button
                    onClick={() => {
                      setShowCancelDialog(false);
                      handleAction('cancel');
                    }}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-3 bg-red-600 text-white text-sm font-semibold rounded-xl active:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    בטל מנוי
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
